import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import { Grid, Paper } from '@material-ui/core';
import InputWrapper from './inputWrapper';
import InputImage from './inputImage';
import Button from '../../common/Button';
import CoursesAPI from '../../../content/CoursesContainer/course-api';
import { Input } from '../../forms';

const InstitutionInput = ({ onClose, onClickAccept, onInstitutionSubmit }) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const readInstitutionFormValues = () => {
    const rawInstitution = values?.institution || {};

    const name =
      typeof rawInstitution === 'object'
        ? rawInstitution.name || ''
        : '';
    const logo =
      typeof rawInstitution === 'object'
        ? rawInstitution.logo ?? null
        : null;
    const clientId =
      typeof rawInstitution === 'object'
        ? rawInstitution.clientId
        : undefined;

    return {
      name: name ? name.trim() : '',
      logo,
      clientId,
    };
  };

  const extractLogoData = (logo) => {
    if (!logo) {
      return null;
    }

    if (typeof logo === 'string') {
      return logo;
    }

    if (typeof logo === 'object' && typeof logo.data === 'string') {
      return logo.data;
    }

    return null;
  };

  const handleAccept = async () => {
    if (isSubmitting) {
      return;
    }

    const { name, logo, clientId } = readInstitutionFormValues();

    if (!name) {
      setErrorMessage('Ingresa el nombre de la institucion.');
      return;
    }

    const logoData = extractLogoData(logo);

    if (!logoData) {
      setErrorMessage('Carga el logo de la institucion.');
      return;
    }

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const payload = {
        name,
        logo: logoData,
        ...(clientId ? { clientId } : {}),
      };

      const response = await CoursesAPI.createInstitution(payload);
      const responseData = response?.data ?? response;
      const rawInstitution =
        (responseData &&
          typeof responseData === 'object' &&
          !Array.isArray(responseData) &&
          responseData.institution &&
          typeof responseData.institution === 'object'
          ? responseData.institution
          : responseData?.data && typeof responseData.data === 'object'
          ? responseData.data
          : responseData) || {};

      let parsedId =
        rawInstitution.id ??
        rawInstitution.institutionId ??
        rawInstitution.ID ??
        payload.id;

      parsedId =
        parsedId !== undefined && parsedId !== null && parsedId !== ''
          ? String(parsedId)
          : undefined;

      let normalizedInstitution = {
        id: parsedId,
        name: rawInstitution.name ?? name,
        value:
          rawInstitution.value ??
          rawInstitution.name ??
          name,
        logo: rawInstitution.logo ?? logoData,
        clientId: rawInstitution.client ?? clientId,
      };

      if (!normalizedInstitution.id) {
        try {
          const institutionsResponse = await CoursesAPI.getInstitution();
          const institutionsData = Array.isArray(institutionsResponse?.data)
            ? institutionsResponse.data
            : [];
          const normalizedNameLower = normalizedInstitution.name
            ? normalizedInstitution.name.toLowerCase().trim()
            : '';
          const matchedInstitution = institutionsData.find((inst) => {
            const instName = inst.name ? inst.name.toLowerCase().trim() : '';
            const instValue = inst.value ? inst.value.toLowerCase().trim() : '';
            const instLabel = inst.label ? inst.label.toLowerCase().trim() : '';

            return (
              (instName && instName === normalizedNameLower) ||
              (instValue && instValue === normalizedNameLower) ||
              (instLabel && instLabel === normalizedNameLower)
            );
          });

          if (matchedInstitution) {
            normalizedInstitution = {
              id:
                matchedInstitution.id !== undefined &&
                matchedInstitution.id !== null &&
                matchedInstitution.id !== ''
                  ? String(matchedInstitution.id)
                  : matchedInstitution.id,
              name: matchedInstitution.name ?? normalizedInstitution.name,
              value:
                matchedInstitution.value ??
                matchedInstitution.name ??
                normalizedInstitution.name,
              logo:
                matchedInstitution.logo !== undefined &&
                matchedInstitution.logo !== null
                  ? matchedInstitution.logo
                  : normalizedInstitution.logo,
              clientId:
                matchedInstitution.client !== undefined
                  ? matchedInstitution.client
                  : normalizedInstitution.clientId,
            };
          }
        } catch (fetchError) {
          // Ignore fetch errors. We'll keep the normalizedInstitution as-is.
        }
      }

      const formFieldValue =
        normalizedInstitution.id ??
        normalizedInstitution.value ??
        normalizedInstitution.name ??
        '';

      setFieldValue(
        'institution',
        formFieldValue !== undefined && formFieldValue !== null
          ? String(formFieldValue)
          : ''
      );
      setFieldTouched('institution', true, false);

      if (onClickAccept) {
        onClickAccept(normalizedInstitution);
      }

      if (onInstitutionSubmit) {
        onInstitutionSubmit(normalizedInstitution, setFieldValue);
      }
    } catch (error) {
      const message =
        error?.data?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Ocurrio un error al crear la institucion.';
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper style={{ padding: '20px', margin: 'auto', maxWidth: 600 }}>
      <div>
        <Grid container spacing={2} direction="column">
          <strong>Agregar nueva institucion</strong>
          <Grid item>
            <InputWrapper id="institution.name" name="institution.name">
              <Input
                id="institution.name"
                name="institution.name"
                fullWidth
                label="Ingresa nombre de la institucion"
                variant="outlined"
              />
            </InputWrapper>
          </Grid>
          <Grid item>
            <InputWrapper
              variant="big"
              name="institution.logo"
              id="institution.logo"
              label="Cargar logo de institucion"
              description="El archivo debe estar en escala de grises y medir 150x50px como maximo. Formatos permitidos JPG PNG SVG."
            >
              <InputImage
                iconName="cloud_upload"
                name="institution.logo"
                id="institution.logo"
                allowedExts={[
                  'image/png',
                  'image/jpg',
                  'image/jpeg',
                  'image/svg+xml',
                ]}
              />
            </InputWrapper>
          </Grid>
        </Grid>
        {errorMessage && (
          <span className="error-message" style={{ marginTop: '8px' }}>
            {errorMessage}
          </span>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            marginTop: 16,
          }}
        >
          <Button
            variant="outlined"
            color="default"
            type="button"
            secondary
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            color="primary"
            onClick={handleAccept}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Guardando...' : 'Guardar institucion'}
          </Button>
        </div>
      </div>
    </Paper>
  );
};

export default InstitutionInput;



